<template>
  <v-dialog v-model="showModal" max-width="800px" persistent>
    <v-card>
      <v-card-title class="modal-header">
        <span>Bulk Edit Equipment ({{ itemCount }} items)</span>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert type="info" variant="tonal" class="mb-4">
          Only fill in the fields you want to update. Empty fields will be left unchanged.
        </v-alert>

        <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
          {{ error }}
        </v-alert>

        <v-form ref="formRef">
          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="localFormData.status"
                :items="statusOptions"
                label="Status (optional)"
                variant="outlined"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.location"
                label="Location (optional)"
                variant="outlined"
                placeholder="e.g., Storage Room A"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="localFormData.rating"
                label="Rating 1-10 (optional)"
                type="number"
                min="1"
                max="10"
                variant="outlined"
                placeholder="Rate 1-10"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="localFormData.os"
                :items="osOptions"
                label="Operating System (optional)"
                variant="outlined"
                placeholder="Select or type OS"
                clearable
              ></v-combobox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.ram"
                label="RAM (optional)"
                variant="outlined"
                placeholder="e.g., 16GB"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.storage"
                label="Storage (optional)"
                variant="outlined"
                placeholder="e.g., 512GB SSD"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.cpu"
                label="CPU (optional)"
                variant="outlined"
                placeholder="e.g., Intel i5"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.gpu"
                label="GPU (optional)"
                variant="outlined"
                placeholder="e.g., Intel UHD"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="localFormData.value"
                label="Value ($) (optional)"
                type="number"
                min="0"
                step="0.01"
                variant="outlined"
                prefix="$"
                placeholder="e.g., 250.00"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.donated_on"
                label="Donation Date (optional)"
                type="date"
                variant="outlined"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-combobox
                v-model="localFormData.charger"
                :items="chargerOptions"
                label="Charger (optional)"
                variant="outlined"
                placeholder="Select or type charger"
                clearable
              ></v-combobox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-combobox
                v-model="localFormData.donor"
                :items="donorOptions"
                label="Donor (optional)"
                variant="outlined"
                placeholder="Person or organization"
                clearable
              ></v-combobox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="localFormData.comments"
                label="Comments (optional)"
                variant="outlined"
                rows="3"
                placeholder="Add notes or comments..."
                clearable
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          variant="text"
          @click="$emit('close')"
          :disabled="submitting"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="handleSubmit"
          :loading="submitting"
        >
          Apply Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, watch, onMounted } from 'vue'
import { api } from '@/composables/api'

interface BulkEditFormData {
  status?: string
  location?: string
  rating?: number | null
  os?: string
  ram?: string
  storage?: string
  cpu?: string
  gpu?: string
  charger?: string
  comments?: string
  value?: number | null
  donor?: string
  donated_on?: string
}

interface Props {
  show: boolean
  itemCount: number
  submitting?: boolean
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  itemCount: 0,
  submitting: false,
  error: ''
})

const emit = defineEmits<{
  'close': []
  'submit': [data: BulkEditFormData]
}>()

const showModal = ref(props.show)
const formRef = ref<any>(null)
const donorOptions = ref<string[]>([])
const chargerOptions = ref<string[]>([])

const loadDonors = async () => {
  try {
    const response = await api.get('/equipment/donors/list')
    const donors = response.data || response || []
    donorOptions.value = donors.map((d: any) => d.donor).filter(Boolean)
  } catch (error) {
    console.error('Error loading donors:', error)
    donorOptions.value = []
  }
}

const loadChargers = async () => {
  try {
    const response = await api.get('/equipment/chargers/list')
    const chargers = response.data || response || []
    chargerOptions.value = chargers.map((c: any) => c.charger).filter(Boolean)
  } catch (error) {
    console.error('Error loading chargers:', error)
    chargerOptions.value = []
  }
}

onMounted(() => {
  loadDonors()
  loadChargers()
})

const localFormData = ref<BulkEditFormData>({
  status: undefined,
  location: undefined,
  rating: null,
  os: undefined,
  ram: undefined,
  storage: undefined,
  cpu: undefined,
  gpu: undefined,
  charger: undefined,
  comments: undefined,
  value: null,
  donor: undefined,
  donated_on: undefined
})

const statusOptions = [
  { title: 'Processing', value: 'processing' },
  { title: 'Troubleshooting', value: 'troubleshooting' },
  { title: 'Parts', value: 'parts' },
  { title: 'Available', value: 'available' },
  { title: 'Claimed', value: 'claimed' },
  { title: 'Delivered', value: 'delivered' },
  { title: 'Retired', value: 'retired' }
]

const osOptions = [
  'Windows',
  'macOS',
  'Chrome OS',
  'Linux',
  'Linux (Mint)',
  'Linux (Zorin)',
  'Linux (Ubuntu)'
]

watch(() => props.show, (newVal) => {
  showModal.value = newVal
  if (newVal) {
    // Reset form when modal opens
    localFormData.value = {
      status: undefined,
      location: undefined,
      rating: null,
      os: undefined,
      ram: undefined,
      storage: undefined,
      cpu: undefined,
      gpu: undefined,
      comments: undefined,
      value: null,
      donor: undefined,
      donated_on: undefined
    }
  }
})

watch(showModal, (newVal) => {
  if (!newVal) {
    emit('close')
  }
})

const handleSubmit = async () => {
  // Filter out undefined/null values - only send fields that user filled in
  const updateData: BulkEditFormData = {}

  if (localFormData.value.status !== undefined && localFormData.value.status !== null) {
    updateData.status = localFormData.value.status
  }
  if (localFormData.value.location !== undefined && localFormData.value.location !== null && localFormData.value.location !== '') {
    updateData.location = localFormData.value.location
  }
  if (localFormData.value.rating !== undefined && localFormData.value.rating !== null) {
    updateData.rating = localFormData.value.rating
  }
  if (localFormData.value.os !== undefined && localFormData.value.os !== null) {
    updateData.os = localFormData.value.os
  }
  if (localFormData.value.ram !== undefined && localFormData.value.ram !== null && localFormData.value.ram !== '') {
    updateData.ram = localFormData.value.ram
  }
  if (localFormData.value.storage !== undefined && localFormData.value.storage !== null && localFormData.value.storage !== '') {
    updateData.storage = localFormData.value.storage
  }
  if (localFormData.value.cpu !== undefined && localFormData.value.cpu !== null && localFormData.value.cpu !== '') {
    updateData.cpu = localFormData.value.cpu
  }
  if (localFormData.value.gpu !== undefined && localFormData.value.gpu !== null && localFormData.value.gpu !== '') {
    updateData.gpu = localFormData.value.gpu
  }
  if (localFormData.value.charger !== undefined && localFormData.value.charger !== null && localFormData.value.charger !== '') {
    updateData.charger = localFormData.value.charger
  }
  if (localFormData.value.comments !== undefined && localFormData.value.comments !== null && localFormData.value.comments !== '') {
    updateData.comments = localFormData.value.comments
  }
  const bulkValue = localFormData.value.value
  if (bulkValue !== undefined && bulkValue !== null && (bulkValue as any) !== '' && !Number.isNaN(bulkValue)) {
    updateData.value = bulkValue
  }
  if (localFormData.value.donor !== undefined && localFormData.value.donor !== null && localFormData.value.donor !== '') {
    updateData.donor = localFormData.value.donor
  }
  if (localFormData.value.donated_on !== undefined && localFormData.value.donated_on !== null && localFormData.value.donated_on !== '') {
    updateData.donated_on = localFormData.value.donated_on
  }

  emit('submit', updateData)
}
</script>

<style scoped>
.modal-header {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  font-weight: 600;
  font-size: 1.25rem;
}
</style>
